import { useState } from "react";
import { Eye, Music, Video } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";

function isLottie(url: string) {
  return url.endsWith(".json") || url.endsWith(".lottie");
}

function isAudio(url: string) {
  return /\.(mp3|wav|ogg|m4a|aac|flac|webm)$/i.test(url);
}

function isVideo(url: string) {
  return /\.(mp4|webm|mov|avi|mkv|m4v|ogv)$/i.test(url);
}

interface MediaPreviewButtonProps {
  url: string;
  /** Button size variant */
  size?: "icon" | "sm";
}

export default function MediaPreviewButton({ url, size = "icon" }: MediaPreviewButtonProps) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        variant="ghost"
        size={size === "sm" ? "sm" : "icon"}
        className={size === "icon" ? "h-9 w-9 shrink-0" : "h-9 shrink-0"}
        onClick={() => setOpen(true)}
        title="Prévisualiser"
      >
        <Eye className="h-3.5 w-3.5" />
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-[440px] p-4">
          <DialogHeader>
            <DialogTitle className="text-sm truncate">
              {url.split("/").pop()}
            </DialogTitle>
            <DialogDescription className="sr-only">Aperçu du média</DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-center min-h-[200px] max-h-[400px] overflow-hidden rounded-md bg-muted/30">
            {isVideo(url) ? (
              <video src={url} controls className="max-w-full max-h-[380px]" preload="metadata" />
            ) : isAudio(url) ? (
              <div className="flex flex-col items-center gap-4 p-6 w-full">
                <Music className="h-12 w-12 text-muted-foreground" />
                <audio src={url} controls className="w-full" preload="metadata" />
              </div>
            ) : isLottie(url) ? (
              <DotLottieReact src={url} loop autoplay style={{ width: 380, height: 380 }} />
            ) : (
              <img src={url} alt="" className="max-w-full max-h-[380px] object-contain" />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
